
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SearchInput from '../../components/base/SearchInput';
import { featuredPlaylists } from '../../mocks/musicData';
import { useMusicPlayer } from '../../hooks/useMusicPlayer';

export default function PlaylistsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const { playTrack } = useMusicPlayer();
  const navigate = useNavigate();

  const categories = ['All', 'Pop', 'Rock', 'Hip Hop', 'Electronic', 'Jazz', 'Classical'];

  const filteredPlaylists = featuredPlaylists.filter(playlist => {
    const matchesSearch = playlist.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         playlist.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || playlist.genre === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handlePlayPlaylist = (playlist: any) => {
    if (playlist.tracks && playlist.tracks.length > 0) {
      playTrack(playlist.tracks[0]);
    }
  };

  const handleTrackSelect = (track: any) => {
    playTrack(track);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onTrackSelect={handleTrackSelect} />
      
      <main className="pt-8 pb-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-purple-600 to-pink-600 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold text-white mb-4">All Playlists</h1>
            <p className="text-xl text-purple-100 mb-8">Discover curated collections for every mood and moment</p>
            
            <div className="max-w-md mx-auto">
              <SearchInput 
                onSearch={setSearchQuery}
                placeholder="Search playlists..."
                className="bg-white"
              />
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    selectedCategory === category
                      ? 'bg-purple-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-purple-50 hover:text-purple-600'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Playlists Grid */}
        <section className="pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {filteredPlaylists.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
                {filteredPlaylists.map((playlist) => (
                  <div 
                    key={playlist.id} 
                    className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer group"
                  >
                    <div className="relative overflow-hidden rounded-t-xl">
                      <img 
                        src={playlist.imageUrl}
                        alt={playlist.title}
                        className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayPlaylist(playlist);
                          }}
                          className="w-12 h-12 bg-purple-600 hover:bg-purple-700 text-white rounded-full opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300 flex items-center justify-center cursor-pointer"
                        >
                          <i className="ri-play-fill text-lg"></i>
                        </button>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-1 truncate">{playlist.title}</h3>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{playlist.description}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{playlist.trackCount} tracks</span>
                        <span>{playlist.plays} plays</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <i className="ri-music-2-line text-6xl text-gray-300 mb-4 block"></i>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No playlists found</h3>
                <p className="text-gray-500">Try adjusting your search or category filter</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
